import moment from "moment";
import axios from "axios";
import { toPairs } from "lodash";
import { toast } from "react-toastify";
import { s3AutomationPrivate } from "../../../../../utils/aws";
import {
  uploadFileToS3, getUser, getFileFromS3, getAppVersion, convertUnit8ArrayToJson, getPresignedURL, pause,
} from "../../../../../utils/helpers";

export const parentFolder = "media/chem_history/";

/**
 * Create json file's aws path
 * @param {Object} dateObj current date object (utc)
 * @returns {String} json file path
 */
const createS3JsonFileKey = (dateObj) => {
  const key = `${parentFolder}json/${dateObj.year}/${dateObj.month}.json`;
  return key;
};

const splitFileNameAndExtension = (fileName) => {
  // get the last dot in the filename
  const dotIndex = fileName.lastIndexOf(".");

  // if there's no dot, return the original filename and an empty string for the extension
  if (dotIndex === -1) {
    return [fileName, ""];
  }

  // split the filename and extension based on the dot
  const name = fileName.substring(0, dotIndex);
  const extension = `.${fileName.substring(dotIndex + 1)}`;

  // return an array with the filename and extension
  return [name, extension];
};

/**
 * Create excel file's aws key
 * @param {Object} dateObj current date object (utc)
 * @returns {String} excel file key
 */
const createS3ExcelFileKey = (fileName, dateObj) => {
  const defaultPath = `${parentFolder}excel/${dateObj.year}/${dateObj.month}/`;
  const nameSplit = splitFileNameAndExtension(fileName);
  const newFileName = `${nameSplit[0]}_${dateObj.timeStamp}${nameSplit[1]}`;
  const key = defaultPath + newFileName;
  return key;
};

/**
 * Formats excel file and api info into json
 * @param {String} excelPath excel file path
 * @param {String} excelFileName excel filename
 * @param {Object} dateObj current date object (utc)
 * @param {String} selectedOpt test name
 * @param {Object} apiResp api response object
 * @param {String} fapPdfPath if the test is FAP, this is the file path to the fap pdf
 * @param {Boolean} isFirstData if true, it's the first data for that day, create the day key
 * @returns {Object | String} if isFirstData, returns stringified jsonData, if not, returns jsonData object
 */
const createS3JsonData = (excelPath, excelFileName, dateObj, selectedOpt, apiResp, fapPdfPath, isFirstData) => {
  const userDetails = getUser();
  let jsonData = {
    user: userDetails.email,
    role: userDetails.role,
    version: getAppVersion(),
    app: "MySql",
    api: apiResp,
    excel_file_name: excelFileName,
    excel_file_path: excelPath,
    uploaded_at: dateObj.timeStamp,
    selected_test: selectedOpt,
  };

  if (typeof fapPdfPath === "string") {
    jsonData.fap_file_path = fapPdfPath;
  }

  if (isFirstData) {
    jsonData = JSON.stringify({
      [dateObj.day]: [jsonData],
    });
  }
  // console.log("create json data ", dateObj, ", data: ", jsonData);
  return jsonData;
};

/**
 * Uploads new json file to aws
 * @param {String} excelPath excel file path
 * @param {String} excelFileName excel filename
 * @param {String} jsonKey json key
 * @param {Object} dateObj current date object (utc)
 * @param {String} selectedOpt test name
 * @param {Object} apiResp api response object
 * @param {String} fapPdfPath if the test is FAP, this is the file path to the fap pdf
 */
const createNewS3JsonFile = async (excelPath, excelFileName, jsonKey, dateObj, selectedOpt, apiResp, fapPdfPath) => {
  const jsonData = createS3JsonData(excelPath, excelFileName, dateObj, selectedOpt, apiResp, fapPdfPath, true);
  const file = new Blob([jsonData], { type: "application/json" });
  const fileObj = {
    file,
    key: jsonKey,
    type: "application/json",
  };
  await uploadFileToS3(fileObj, s3AutomationPrivate);
  // if(response.success) {
  //   toast.success("File uploaded to AWS successfully")
  // } else {
  //   toast.error("Failed to upload the File to AWS")
  // }
};

/**
 * Appends new data to existing json file in aws
 * @param {String} excelPath excel file path
 * @param {String} excelFileName excel filename
 * @param {String} jsonKey json key
 * @param {String} existingData stringified existing data object
 * @param {Object} dateObj current date object (utc)
 * @param {String} selectedOpt test name
 * @param {Object} apiResp api response object
 * @param {String} fapPdfPath if the test is FAP, this is the file path to the fap pdf
 */
const appendDataToS3JsonFile = async (excelPath, excelFileName, jsonKey, existingData, dateObj, selectedOpt, apiResp, fapPdfPath) => {
  let currentMonthData = convertUnit8ArrayToJson(existingData);
  const newData = createS3JsonData(excelPath, excelFileName, dateObj, selectedOpt, apiResp, fapPdfPath, false);
  if (currentMonthData[dateObj.day]) {
    currentMonthData[dateObj.day].push(newData);
  } else {
    currentMonthData[dateObj.day] = [newData];
  }
  currentMonthData = JSON.stringify(currentMonthData);
  const file = new Blob([currentMonthData], { type: "application/json" });
  const fileObj = {
    file,
    key: jsonKey,
    type: "application/json",
  };
  await uploadFileToS3(fileObj, s3AutomationPrivate);
};

const uploadExcelToS3 = async (file, dateObj) => {
  const excelKey = createS3ExcelFileKey(file.name, dateObj);
  const fileObj = {
    file,
    key: excelKey,
    type: file.type,
  };

  // console.log("uploading excel: ", file);
  return uploadFileToS3(fileObj, s3AutomationPrivate);
};

/** Same date object shape used by chemistry history excel + JSON keys. */
export const buildChemHistoryDateObj = () => {
  const m = moment();
  return {
    year: m.year(),
    month: m.format("MM"),
    day: m.format("DD"),
    timeStamp: m.format("YYYY-MM-DD_HHmmss"),
  };
};

/** Uploads an excel to the standard chemistry history prefix (`media/chem_history/excel/...`). */
export const uploadChemHistoryExcelFile = async (file) => {
  const dateObj = buildChemHistoryDateObj();
  return uploadExcelToS3(file, dateObj);
};

/** Uploads multiple chemistry history excels using one presigned-url API call. */
export const uploadChemHistoryExcelFiles = async (files) => {
  const validFiles = (files || []).filter(Boolean);
  if (!validFiles.length) {
    return { success: true, uploads: [] };
  }

  const dateObj = buildChemHistoryDateObj();
  const uploadItems = validFiles.map((file, index) => ({
    file,
    path: createS3ExcelFileKey(file.name, dateObj).replace(/(\.[^.]+)?$/, `_${index + 1}$1`),
  }));

  const presignedData = await getPresignedURL({
    action: "post",
    urls: uploadItems.map((item) => ({
      Bucket: s3AutomationPrivate.bucket,
      Name: item.path,
      Region: s3AutomationPrivate.region,
      ContentType: item.file.type,
    })),
  });

  if (!presignedData?.success) {
    return { success: false, uploads: [] };
  }

  const maxRetries = 3;
  const uploadWithRetry = async (presignedEntry, file, path, attempt = 0) => {
    const formData = new FormData();
    toPairs(presignedEntry.fields).forEach(([fieldKey, fieldValue]) => {
      if (fieldKey !== "Content-Type") {
        formData.append(fieldKey, fieldValue);
      }
    });
    formData.append("file", file);
    try {
      await axios.post(presignedEntry.url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return { success: true, path, type: file.type || "" };
    } catch (error) {
      const isRetryable = error?.response?.status === 503;
      if (!isRetryable || attempt + 1 >= maxRetries) {
        return { success: false, path, type: file.type || "" };
      }
      await pause(1000);
      return uploadWithRetry(presignedEntry, file, path, attempt + 1);
    }
  };

  const uploadResponses = await Promise.all(
    uploadItems.map(async (item) => {
      const presignedEntry = presignedData.result?.[item.path];
      if (!presignedEntry?.url || !presignedEntry?.fields) {
        return { success: false, path: item.path, type: item.file.type || "" };
      }
      return uploadWithRetry(presignedEntry, item.file, item.path);
    }),
  );

  return {
    success: uploadResponses.every((response) => response?.success),
    uploads: uploadResponses,
  };
};

/**
 * Uploads excel path and api data as a json file to aws
 * @param {String} excelPath excel file path
 * @param {String} excelFileName excel filename
 * @param {Object} dateObj current date object (utc)
 * @param {String} selectedOpt test name
 * @param {Object} apiResp api response object
 * @param {String} fapPdfPath if the test is FAP, this is the file path to the fap pdf
 */
const uploadDataToS3Json = async (excelPath, excelFileName, dateObj, selectedOpt, apiResp, fapPdfPath) => {
  const jsonKey = createS3JsonFileKey(dateObj);
  const response = await getFileFromS3(jsonKey, s3AutomationPrivate);
  if (response.success) {
    appendDataToS3JsonFile(excelPath, excelFileName, jsonKey, response.data, dateObj, selectedOpt, apiResp, fapPdfPath);
  } else if (response.errorCode === "NoSuchKey") {
    createNewS3JsonFile(excelPath, excelFileName, jsonKey, dateObj, selectedOpt, apiResp, fapPdfPath);
  }
};

/**
 * Takes in raw api response and formats it for the json file
 * @param {Object} raw unmodified response object from the api
 * @returns {Object} formatted api response object
 */
const formatAPIResponse = (raw) => {
  const res = {
    message: raw.error_message ? raw.error_message : raw.message,
    api_name: `/${raw.api_name}`,
    status_code: raw.status_code,
    response: raw.result ?? {},
  };
  // console.log("raw api response ", raw, "formatted", res)
  return res;
};

/**
 * Upload excel file and json api response to AWS
 * @param {File} file File object
 * @param {Object} apiRawResponse api response
 * @param {String} selectedOpt chem test name
 */
export const moveToHistory = async (file, apiRawResponse, selectedOpt, fapPdfPath, preUploadedExcelPath = null) => {
  try {
    const dateObj = buildChemHistoryDateObj();
    const formattedResp = apiRawResponse ? formatAPIResponse(apiRawResponse) : {};
    const uploadExcelResp = preUploadedExcelPath
      ? { success: true, path: preUploadedExcelPath }
      : await uploadExcelToS3(file, dateObj);
    // Success & data needed to be recheck
    if (uploadExcelResp.success) {
      await uploadDataToS3Json(uploadExcelResp.path, file.name, dateObj, selectedOpt, formattedResp, fapPdfPath);
    }
  } catch (e) {
    toast.error("Failed to save upload history.");
  }
};

/**
 * Records multiple already-uploaded files into chemistry history using one JSON read/write cycle.
 * @param {Array<{fileName: string, excelPath: string}>} historyItems
 * @param {Object} apiRawResponse
 * @param {String} selectedOpt
 * @param {String} fapPdfPath
 */
export const moveBatchToHistory = async (historyItems, apiRawResponse, selectedOpt, fapPdfPath = null) => {
  try {
    const validHistoryItems = (historyItems || []).filter(
      (historyItem) => historyItem?.fileName && historyItem?.excelPath,
    );
    if (!validHistoryItems.length) {
      return;
    }

    const dateObj = buildChemHistoryDateObj();
    const formattedResponse = apiRawResponse ? formatAPIResponse(apiRawResponse) : {};
    const jsonKey = createS3JsonFileKey(dateObj);
    const existingJsonFileResponse = await getFileFromS3(jsonKey, s3AutomationPrivate);

    let currentMonthData = {};
    if (existingJsonFileResponse.success) {
      currentMonthData = convertUnit8ArrayToJson(existingJsonFileResponse.data);
    } else if (existingJsonFileResponse.errorCode !== "NoSuchKey") {
      return;
    }

    if (!currentMonthData[dateObj.day]) {
      currentMonthData[dateObj.day] = [];
    }

    validHistoryItems.forEach((historyItem) => {
      const historyEntry = createS3JsonData(
        historyItem.excelPath,
        historyItem.fileName,
        dateObj,
        selectedOpt,
        formattedResponse,
        fapPdfPath,
        false,
      );
      currentMonthData[dateObj.day].push(historyEntry);
    });

    const updatedJsonFile = new Blob([JSON.stringify(currentMonthData)], {
      type: "application/json",
    });
    await uploadFileToS3(
      {
        file: updatedJsonFile,
        key: jsonKey,
        type: "application/json",
      },
      s3AutomationPrivate,
    );
  } catch (e) {
    toast.error("Failed to save upload history.");
  }
};
